/**
 * V4.20 — Importar Boletos Safra (admin)
 * /v2/financeiro/dnapay/importar-boletos
 */
import React, { useRef, useState, useMemo, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle2, AlertTriangle, RefreshCw, Eye, X, Trash2 } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS = {
  match_ok:    { label: 'Match OK',      cls: 'dna-tone-success' },
  sem_aluno:   { label: 'Sem aluno',     cls: 'dna-tone-warning' },
  sem_titulo:  { label: 'Sem título',    cls: 'dna-tone-warning' },
  revisao:     { label: 'Revisão',       cls: 'dna-tone-info' },
  duplicado:   { label: 'Duplicado',     cls: 'dna-tone-neutral' },
  invalido:    { label: 'Inválido',      cls: 'dna-tone-danger' },
};

const fmtBRL = (c) => c == null ? '—' : (Number(c)/100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (iso) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
const mask = (ld) => (ld||'').replace(/(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d)(\d{14}).*/, '$1.$2 $3.$4 $5.$6 $7 $8');

export default function ImportarBoletos() {
  const [files, setFiles] = useState([]);
  const [batch, setBatch] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ match_ok: true, sem_aluno: true, sem_titulo: true, revisao: true, duplicado: false, invalido: false });
  const [progress, setProgress] = useState(null); // {stage, stage_msg, progress_percent, processed_pages, total_pages, boletos_found, matched, review_required, errors_count}
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const runningRef = useRef(false);   // V8.5 · impede 2 jobs/2 pollings para o mesmo clique

  useEffect(() => () => {             // cleanup: nenhum timer sobrevive ao unmount
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    toast.dismiss('poll-retry');
    runningRef.current = false;
  }, []);

  const items = useMemo(() => (batch?.items || []).filter(it => filter[it.status_match]), [batch, filter]);
  const validSelectable = useMemo(() => (batch?.items || []).filter(it => it.status_match !== 'invalido' && it.status_match !== 'duplicado'), [batch]);

  const deleteItem = async (item_id) => {
    if (!batch?.batch_id) return;
    if (!window.confirm('Excluir este item do lote de importação? Esta ação não afeta títulos financeiros existentes.')) return;
    try {
      await api.delete(`/financeiro/boletos-import/batches/${batch.batch_id}/items/${item_id}`);
      // Atualiza estado local removendo o item
      setBatch((b) => ({ ...b, items: b.items.filter((it) => it.item_id !== item_id) }));
      setSelected((s) => { const n = new Set(s); n.delete(item_id); return n; });
      toast.success('Item removido do lote');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao excluir item');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    setFiles((prev) => [...prev, ...list]);
  };
  const pick = (e) => {
    const list = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    setFiles((prev) => [...prev, ...list]);
    e.target.value = '';
  };

  const uploadAndPreview = async () => {
    if (!files.length) { toast.error('Selecione ao menos 1 PDF.'); return; }
    if (runningRef.current) { toast.error('Já existe um processamento em andamento.'); return; }
    runningRef.current = true;
    setBusy(true);
    setProgress({ stage: 'uploading', stage_msg: 'Enviando arquivo...', progress_percent: 0 });
    try {
      // 1) Upload rápido (retorna em segundos)
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const { data: up } = await api.post('/financeiro/boletos-import/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const batch_id = up.batch_id;
      toast.success('Arquivo recebido. Processando em segundo plano.');
      setProgress({ stage: 'queued', stage_msg: 'aguardando processamento', progress_percent: 1, batch_id });
      // 2) Dispara processamento (502/504 aqui não impede o job — o claim é atômico no backend)
      try {
        await api.post(`/financeiro/boletos-import/${batch_id}/process`);
      } catch (err) {
        const st = err?.response?.status ?? 0;
        if (![0, 500, 502, 503, 504].includes(st)) throw err;
      }
      // 3) Polling do status
      await pollStatus(batch_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao enviar PDFs');
      setProgress(null);
    } finally { setBusy(false); runningRef.current = false; }
  };

  // V8.6 · Polling resiliente: backoff exponencial, tolera 502/503/504 sem matar o job
  const pollStatus = async (batch_id) => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    const BASE_MS = 2500, MAX_MS = 30000, MAX_FAILS = 8;
    // V8.8 · 404 também é tratado como transitório: em replica set a leitura pode chegar
    // a um secundário atrasado logo após a criação do job (nunca reenviar o PDF por isso).
    const TRANSIENT = [0, 404, 408, 429, 500, 502, 503, 504];
    let fails = 0;
    let queuedTicks = 0, kicked = false;
    return new Promise((resolve) => {
      const schedule = (ms) => { pollRef.current = setTimeout(tick, ms); };
      const finish = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } resolve(); };
      const tick = async () => {
        try {
          const { data } = await api.get(`/financeiro/boletos-import/${batch_id}/status`);
          if (fails > 0) { toast.dismiss('poll-retry'); fails = 0; }
          setProgress({ ...data, batch_id });
          if (data.stage === 'ready') {
            const { data: full } = await api.get(`/financeiro/boletos-import/${batch_id}/preview`);
            // V4.41 · garante batch_id no objeto (backend salva como `id`)
            const fullWithBatchId = { ...full, batch_id: full.batch_id || full.id || batch_id };
            setBatch(fullWithBatchId);
            const preSel = new Set((fullWithBatchId.items || []).filter(it => it.status_match === 'match_ok' || it.status_match === 'sem_titulo').map(it => it.item_id));
            setSelected(preSel);
            toast.success(`Prévia pronta — ${fullWithBatchId.items.length} boletos identificados`, { id: 'poll-done' });
            return finish();
          }
          if (data.stage === 'error') {
            toast.error('Processamento falhou: ' + (data.stage_msg || ''), { id: 'poll-done' });
            return finish();
          }
          if (data.stage === 'cancelled') {
            toast.info('Lote cancelado.', { id: 'poll-done' });
            return finish();
          }
          if (data.stage === 'queued' && !kicked && ++queuedTicks >= 4) {
            // claim atômico no backend: não duplica job, apenas garante o start
            kicked = true;
            try { await api.post(`/financeiro/boletos-import/${batch_id}/process`); } catch (_) {}
          }
          return schedule(BASE_MS);
        } catch (e) {
          const st = e?.response?.status ?? 0;
          // 404 é tolerado só nas primeiras tentativas (lag de replicação); se persistir,
          // o lote realmente não existe mais — avisa sem reenviar nem recriar job.
          const transient = (TRANSIENT.includes(st) && !(st === 404 && fails >= 3)) || e?.code === 'ECONNABORTED';
          if (st === 404 && fails >= 3) {
            toast.error('Este lote não existe mais (foi excluído). Envie o PDF novamente se necessário.', { id: 'poll-retry' });
            return finish();
          }
          if (!transient) {
            toast.error('Erro no polling: ' + (e?.response?.data?.detail || e.message), { id: 'poll-retry' });
            return finish();
          }
          fails += 1;
          if (fails > MAX_FAILS) {
            toast.error('Servidor não respondeu ao acompanhamento. O processamento continua em segundo plano — reabra a tela para ver o resultado.', { id: 'poll-retry' });
            return finish();
          }
          // 1 único toast ativo, sempre atualizado (id fixo = dedupe)
          toast.loading(`Reconectando ao servidor... (tentativa ${fails}/${MAX_FAILS})`, { id: 'poll-retry' });
          setProgress((p) => ({ ...(p || {}), batch_id, stage: p?.stage || 'processing', stage_msg: 'reconectando ao servidor...' }));
          return schedule(Math.min(BASE_MS * Math.pow(2, fails), MAX_MS));
        }
      };
      schedule(BASE_MS);
    });
  };

  const rematch = async () => {
    const bid = batch?.batch_id || batch?.id;
    if (!bid) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/financeiro/boletos-import/${bid}/rematch`);
      toast.success(`Rematch: ${data.matched} match_ok · ${data.review_required} para revisão`);
      const { data: full } = await api.get(`/financeiro/boletos-import/${bid}/preview`);
      setBatch({ ...full, batch_id: full.batch_id || full.id || bid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no rematch');
    } finally { setBusy(false); }
  };

  const toggle = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAllValid = () => setSelected(new Set(validSelectable.map(it => it.item_id)));

  const confirmImport = async () => {
    const bid = batch?.batch_id || batch?.id;
    // V4.41 — Log de diagnóstico (temporário)
    console.log('CONFIRM_IMPORT_CLICK', {
      selectedCount: selected.size,
      validCount: (batch?.items || []).filter(it => it.linha_digitavel_valida !== false).length,
      batchId: bid,
      batchKeys: batch ? Object.keys(batch) : [],
    });
    if (!bid) { toast.error('Lote sem ID — recarregue a prévia.'); return; }
    if (!selected.size) { toast.error('Selecione ao menos 1 item.'); return; }
    if (busy) return; // impede duplo clique
    setBusy(true);
    try {
      const { data } = await api.post('/financeiro/boletos-import/confirm', {
        batch_id: bid,
        approve_item_ids: Array.from(selected),
      });
      const parts = [];
      if (data.imported) parts.push(`Importados: ${data.imported}`);
      if (data.updated) parts.push(`Atualizados: ${data.updated}`);
      if (data.rejected) parts.push(`Rejeitados: ${data.rejected}`);
      if (data.link_errors) parts.push(`Erros: ${data.link_errors}`);
      if (data.created_titles) parts.push(`Títulos criados: ${data.created_titles}`);
      if (data.created_plans) parts.push(`Planos criados: ${data.created_plans}`);
      toast.success(parts.join(' · ') || 'Importação concluída.');
      // Se houve erros de vinculação, exibe alerta detalhado (não fatal)
      if (data.link_errors && Array.isArray(data.link_error_details) && data.link_error_details.length) {
        const first = data.link_error_details.slice(0, 3).map(e => e.error).join(' | ');
        toast.error(`Alguns itens falharam: ${first}${data.link_error_details.length > 3 ? ' …' : ''}`);
      }
      setBatch(null); setFiles([]); setSelected(new Set()); setProgress(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'erro desconhecido';
      console.error('CONFIRM_IMPORT_ERROR', { status, detail, response: e?.response?.data });
      toast.error(`Falha ao confirmar (${status || 'sem status'}): ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="importar-boletos">
      <div className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><FileText className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Importar Boletos Safra</h1>
          <p className="text-sm text-slate-500">DNAPay · Vincula boletos já emitidos aos títulos financeiros reais dos alunos.</p>
        </div>
      </div>

      {!batch && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="import-dropzone-wrap">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-indigo-500 hover:bg-indigo-50/40 cursor-pointer transition"
            data-testid="import-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-semibold text-slate-700">Arraste PDFs aqui ou clique para selecionar</div>
            <div className="text-xs text-slate-500 mt-1">1 ou vários arquivos · até 20 MB cada</div>
            <input ref={inputRef} type="file" accept="application/pdf" multiple hidden onChange={pick} data-testid="import-file-input" />
          </div>
          {files.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Arquivos selecionados</div>
              <ul className="divide-y divide-slate-100 mb-3">
                {files.map((f, i) => (
                  <li key={i} className="py-2 flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-xs text-slate-500">{(f.size/1024).toFixed(0)} KB</span>
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`remove-file-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
              <button onClick={uploadAndPreview} disabled={busy} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="import-preview-btn">
                {busy ? <RefreshCw className="w-4 h-4 animate-spin inline mr-1" /> : null}
                Gerar prévia
              </button>
            </div>
          )}
        </div>
      )}

      {progress && progress.stage !== 'ready' && (
        <div className="bg-white rounded-2xl border border-indigo-200 p-4 mb-4" data-testid="processing-panel">
          <div className="flex items-center gap-3 mb-3">
            <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                {progress.stage === 'queued' && 'Aguardando processamento...'}
                {progress.stage === 'uploading' && 'Enviando arquivo...'}
                {progress.stage === 'loading_caches' && 'Carregando índices...'}
                {progress.stage === 'processing' && 'Extraindo boletos do PDF...'}
                {progress.stage === 'matching' && 'Vinculando alunos e títulos...'}
                {progress.stage === 'error' && 'Erro no processamento'}
              </div>
              <div className="text-xs text-slate-500">{progress.stage_msg || ''}</div>
            </div>
            <div className="text-lg font-bold text-indigo-600">{progress.progress_percent || 0}%</div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-indigo-600 transition-all duration-300" style={{ width: `${progress.progress_percent || 0}%` }} data-testid="progress-bar" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div className="bg-slate-50 rounded px-2 py-1.5"><span className="text-slate-500">Páginas:</span> <b>{progress.processed_pages || 0}/{progress.total_pages || '?'}</b></div>
            <div className="bg-slate-50 rounded px-2 py-1.5"><span className="text-slate-500">Boletos:</span> <b>{progress.boletos_found || 0}</b></div>
            <div className="bg-emerald-50 rounded px-2 py-1.5"><span className="text-slate-500">Match:</span> <b className="text-emerald-700">{progress.matched || 0}</b></div>
            <div className="bg-amber-50 rounded px-2 py-1.5"><span className="text-slate-500">Revisão:</span> <b className="text-amber-700">{progress.review_required || 0}</b></div>
            <div className="bg-red-50 rounded px-2 py-1.5"><span className="text-slate-500">Erros:</span> <b className="text-red-700">{progress.errors_count || 0}</b></div>
          </div>
        </div>
      )}

      {batch && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
            <div className="flex flex-wrap items-center gap-4">
              {Object.entries(batch.counts).map(([k, v]) => (
                <div key={k} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS[k]?.cls || 'bg-slate-100'}`}>
                  {STATUS[k]?.label || k}: <b>{v}</b>
                </div>
              ))}
              <div className="ml-auto flex items-center gap-2 text-xs">
                {Object.keys(STATUS).map((k) => (
                  <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!filter[k]} onChange={(e) => setFilter({ ...filter, [k]: e.target.checked })} data-testid={`filter-${k}`} />
                    {STATUS[k].label}
                  </label>
                ))}
              </div>
              <button onClick={selectAllValid} className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid="select-all-valid">Selecionar válidos</button>
              <button onClick={rematch} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 inline-flex items-center gap-1" data-testid="rematch-btn"><RefreshCw className="w-3 h-3" /> Refazer correspondências</button>
              <button onClick={() => { setBatch(null); setFiles([]); setSelected(new Set()); setProgress(null); }} className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid="cancel-batch">Cancelar</button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 text-center w-10"><input type="checkbox" checked={selected.size === validSelectable.length && validSelectable.length > 0} onChange={(e) => e.target.checked ? selectAllValid() : setSelected(new Set())} data-testid="sel-all-cb" /></th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Banco</th>
                    <th className="px-3 py-2 text-left">Nosso Nº</th>
                    <th className="px-3 py-2 text-left">Título</th>
                    <th className="px-3 py-2 text-center">Parcela</th>
                    <th className="px-3 py-2 text-left">Aluno</th>
                    <th className="px-3 py-2 text-left">Turma</th>
                    <th className="px-3 py-2 text-left">Responsável</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-center">Vencimento</th>
                    <th className="px-3 py-2 text-right" title="Multa / Mora diária / Desconto">M/Mo/D</th>
                    <th className="px-3 py-2 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan="13" className="text-center text-slate-500 py-8">Nenhum item para os filtros selecionados.</td></tr>
                  )}
                  {items.map((it, idx) => (
                    <tr key={it.item_id} className={idx % 2 ? 'bg-slate-50/60' : ''} data-testid={`row-${it.item_id}`}>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={selected.has(it.item_id)} onChange={() => toggle(it.item_id)} disabled={it.status_match === 'invalido' || it.status_match === 'duplicado'} data-testid={`cb-${it.item_id}`} />
                      </td>
                      <td className="px-3 py-2"><span title={it.match_detail || it.match_reason || ''} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help ${STATUS[it.status_match]?.cls}`} data-testid={`status-${it.item_id}`}>{STATUS[it.status_match]?.label || it.status_match}</span></td>
                      <td className="px-3 py-2">{it.banco_nome || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{it.nosso_numero || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-semibold text-slate-700">{it.categoria || '—'}</div>
                        {it.title_id && <div className="text-emerald-700 font-mono text-[10px] mt-0.5">🔗 {it.title_id.slice(0,8)}…</div>}
                      </td>
                      <td className="px-3 py-2 text-center text-xs font-mono">{it.parcela || '—'}</td>
                      <td className="px-3 py-2">{it.student_name || it.aluno_nome || '—'}</td>
                      <td className="px-3 py-2 text-xs">{it.turma || '—'}</td>
                      <td className="px-3 py-2">{it.guardian_name || it.pagador_nome || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtBRL(it.valor_cents)}</td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">{fmtDate(it.vencimento)}</td>
                      <td className="px-3 py-2 text-right text-[10px] tabular-nums text-slate-600 whitespace-nowrap">
                        {it.multa_cents != null && <div>M: {fmtBRL(it.multa_cents)}</div>}
                        {it.mora_diaria_cents != null && <div>Mo/d: {fmtBRL(it.mora_diaria_cents)}</div>}
                        {it.desconto_cents != null && <div className="text-emerald-700">D: {fmtBRL(it.desconto_cents)}</div>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <a href={`${API_BASE}/financeiro/boletos-import/pdf/${it.file_id}.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 text-[11px] font-medium" data-testid={`view-${it.item_id}`}><Eye className="w-3 h-3" /> PDF</a>
                          <button onClick={() => deleteItem(it.item_id)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-[11px] font-medium" data-testid={`delete-${it.item_id}`} title="Excluir do lote"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-sm text-slate-600">
              <b>{selected.size}</b> selecionados · <b>{validSelectable.length}</b> válidos no lote
            </div>
            <button type="button" onClick={confirmImport} disabled={busy || !selected.size} className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2 relative z-10" data-testid="confirm-import-btn">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {busy ? `Confirmando ${selected.size} ${selected.size === 1 ? 'item' : 'itens'}...` : 'Confirmar importação'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
